import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Company } from '../../../model/company/Company';
import { CompanyService } from '../CompanyService';
import { Button, Card, Col, Form, Image, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { multipartFormData } from '../../../api/services';
import { COMPANY_URL } from '../../../utils/Endpoints';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { getCorrectEnums } from '../../../utils/Functions';
import { calculateFee } from '../../../utils/helpers/FeeCaluculation';
import { copyToClipboard } from '../../../utils/helpers/CopyToClipboard';
import { FaRegCopy } from 'react-icons/fa';

type TCompanyForm = Omit<Partial<Company>, 'updatedAt' | 'createdAt' | 'profilePhoto'>;

export function CompanyForm() {
    const navigate = useNavigate();
    const { companyId }: any = useParams();
    const { country, CompanyEmployeesEnum, CompanyIndustryEnum, PaymentTermsEnum } = useSelector(
        (state: any) => state.enums
    );
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePath, setImage] = useState('');
    const [company, setCompany] = useState<TCompanyForm>({
        createdBy: 0,
        fee: 0.75,
        externalSearchTokens: 0,
        discoveryTokens: 0,
        discoverySearchTokens: 0,
        contact: {
            smallBusiness: false,
        },
        isSupplier: false,
    });
    const canDoDiscovery = company.packageOverride
        ? company.packageOverride.canDoDiscovery
        : company.package?.canDoDiscovery;
    const monthlyDiscoveryTokens = company.packageOverride
        ? company.packageOverride.discoveryTokens
        : company.package?.discoveryTokens;
    const monthlySearchDiscoveryTokens = company.packageOverride
        ? company.packageOverride.discoverySearchTokens
        : company.package?.discoverySearchTokens;

    useEffect(() => {
        if (companyId) {
            CompanyService.getCompany(companyId).then(data => {
                if (data.id) {
                    setCompany({ ...company, ...data });
                    setImage(data.profilePhoto?.path);
                }
            });
        }
    }, []);

    function editCreateCompany() {
        multipartFormData(
            selectedFile,
            companyId ? `${COMPANY_URL}/${companyId}` : COMPANY_URL,
            JSON.stringify(company),
            companyId ? 'put' : 'post'
        )
            .then(() => {
                SuccessToast(companyId ? 'Successfully updated company' : 'Successfully created company');
                navigate(-1);
            })
            .catch(error => ErrorToast(error));
    }

    function handleChange(evt: any, isContact = false, isBank = false) {
        const { value, name } = evt.target;
        if (isContact) {
            setCompany({
                ...company,
                contact: {
                    ...company.contact,
                    [name]: value,
                },
            });
        } else if (isBank) {
            setCompany({
                ...company,
                bank: {
                    ...company.bank,
                    [name]: value,
                },
            });
        } else {
            setCompany({
                ...company,
                [name]: name === 'externalSearchTokens' && value > 100 ? 100 : value,
            });
        }
    }

    function handleCheckbox(evt: any, obj?: string) {
        const { name, checked } = evt.target;

        if (!!obj) {
            setCompany({
                ...company,
                [obj]: {
                    ...company?.[obj as keyof TCompanyForm],
                    [name]: checked,
                },
            });
        } else {
            setCompany({
                ...company,
                [name]: checked,
            });
        }
    }

    function handleFileInput(evt: any) {
        if (evt.target.files.length > 0) {
            setSelectedFile(evt.target.files[0]);
            setImage(URL.createObjectURL(evt.target.files[0]));
        }
    }

    return (
        <>
            <Card className="mb-5">
                <Form as={Card.Body}>
                    <h2>Company info</h2>
                    <hr />
                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Display name</Form.Label>
                            <Form.Control
                                name="displayName"
                                onChange={handleChange}
                                defaultValue={company?.displayName}
                            />
                        </Form.Group>
                        {company?.stripeCustomerId && (
                            <Form.Group as={Col}>
                                <Form.Label>Stripe Customer ID</Form.Label>
                                <div className="d-flex">
                                    <Form.Control value={company.stripeCustomerId} disabled />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => copyToClipboard(company.stripeCustomerId ?? '')}
                                    >
                                        <FaRegCopy className="text-muted" size={20} />
                                    </Button>
                                </div>
                            </Form.Group>
                        )}

                        <Form.Group className="col-md-6 d-flex">
                            {imagePath && (
                                <Image
                                    width={70}
                                    height={70}
                                    alt={imagePath}
                                    src={imagePath}
                                    className="img-cover shadow"
                                />
                            )}
                            <div className="ml-4 w-100">
                                <Form.Label>Upload photo</Form.Label>
                                <Form.Control type="file" onChange={handleFileInput} />
                            </div>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group className="col-md-6">
                            <Form.Label>Industry</Form.Label>
                            <Form.Select name="industry" onChange={handleChange} value={company?.industry ?? ''}>
                                {CompanyIndustryEnum?.map((industry: string) => (
                                    <option key={industry} value={industry}>
                                        {getCorrectEnums(industry)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {/*<Form.Group as={Col}>*/}
                        {/*    <Form.Label>External search tokens</Form.Label>*/}
                        {/*    <Form.Control*/}
                        {/*        max={100}*/}
                        {/*        type="number"*/}
                        {/*        name="externalSearchTokens"*/}
                        {/*        onChange={handleChange}*/}
                        {/*        value={company?.externalSearchTokens}*/}
                        {/*    />*/}
                        {/*</Form.Group>*/}
                        <Form.Group as={Col}>
                            <Form.Label>Fee Currently: {calculateFee(company.fee)}</Form.Label>
                            <Form.Control name="fee" disabled={true} onChange={handleChange} value={company?.fee} />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="col-md-6">
                            <Form.Label>Employees</Form.Label>
                            <Form.Select name="nbEmployees" onChange={handleChange} value={company?.nbEmployees ?? ''}>
                                {CompanyEmployeesEnum?.map((employee: string) => (
                                    <option key={employee} value={employee}>
                                        {getCorrectEnums(employee)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>
                                Discovery profile tokens {canDoDiscovery ? `(${monthlyDiscoveryTokens})` : ''}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="discoveryTokens"
                                onChange={handleChange}
                                value={company?.discoveryTokens}
                            />
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>
                                Discovery search tokens {canDoDiscovery ? `(${monthlySearchDiscoveryTokens})` : ''}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="discoverySearchTokens"
                                onChange={handleChange}
                                disabled={!canDoDiscovery}
                                value={company?.discoverySearchTokens}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="col-md-6">
                            <Form.Label>Payment terms duration</Form.Label>
                            <Form.Select name="paymentTerms" value={company?.paymentTerms ?? ''} disabled={true}>
                                {PaymentTermsEnum?.map((term: string) => (
                                    <option key={term} value={term}>
                                        {getCorrectEnums(term)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Payment terms description</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="paymentTermsCustom"
                                rows={2}
                                maxLength={1000}
                                disabled={true}
                                defaultValue={company?.paymentTermsCustom}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group className="col-3 d-flex mb-3">
                            <Form.Check
                                onChange={e => handleCheckbox(e)}
                                name="isSupplier"
                                label="Is Supplier"
                                checked={company?.isSupplier}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Transparent Pricing"
                                name="isTransparentPricing"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.isTransparentPricing}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Manage Public"
                                name="isManageShared"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.isManageShared}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="First offer accept"
                                name="isFirstOfferAcceptEnabled"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.isFirstOfferAcceptEnabled}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Can View Purchase Order"
                                name="canViewPurchaseOrder"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.canViewPurchaseOrder}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Can send offer proposal"
                                name="canSentOfferProposal"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.canSentOfferProposal}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Can skip reporting"
                                name="canSkipReporting"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.canSkipReporting}
                            />
                        </Form.Group>
                        <Form.Group className="col-3 mb-0">
                            <Form.Check
                                label="Can do annex"
                                name="canDoAnnex"
                                onChange={(e: any) => handleCheckbox(e)}
                                checked={company.canDoAnnex}
                            />
                        </Form.Group>
                    </Row>
                </Form>
            </Card>

            <Card className="mb-5">
                <Form as={Card.Body}>
                    <h2>Contact</h2>
                    <hr />
                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                name="phone"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.phone ?? ''}
                            />
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>Street, House Number, Apartment Number</Form.Label>
                            <Form.Control
                                name="street"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.street ?? ''}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Postal Code</Form.Label>
                            <Form.Control
                                name="zip"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.zip ?? ''}
                            />
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>City</Form.Label>
                            <Form.Control
                                name="city"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.city ?? ''}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>State</Form.Label>
                            <Form.Control
                                name="province"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.province ?? ''}
                            />
                        </Form.Group>
                        <Form.Group as={Col} controlId="formGridState">
                            <Form.Label>Country</Form.Label>
                            <Form.Select
                                name="country"
                                onChange={e => handleChange(e, true)}
                                value={company?.contact?.country ?? ''}
                            >
                                {country.map((country: string) => (
                                    <option key={country}>{country}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Company name</Form.Label>
                            <Form.Control
                                name="companyName"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.companyName ?? ''}
                            />
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>Company register</Form.Label>
                            <Form.Control
                                name="companyRegister"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.companyRegister ?? ''}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Package expiration</Form.Label>
                            <Form.Control
                                type="date"
                                placeholder="expiration"
                                className="autocomplete-input"
                                name="packageExpiration"
                                onChange={e => handleChange(e)}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Vat number</Form.Label>
                            <Form.Control
                                name="uidNumber"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.uidNumber ?? ''}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Invoice firstname</Form.Label>
                            <Form.Control
                                name="invoiceFirstName"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.invoiceFirstName ?? ''}
                            />
                        </Form.Group>

                        <Form.Group as={Col}>
                            <Form.Label>Invoice lastname</Form.Label>
                            <Form.Control
                                name="invoiceLastName"
                                onChange={e => handleChange(e, true)}
                                defaultValue={company?.contact?.invoiceLastName ?? ''}
                            />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col} className="mt-3 mb-0" id="formGridCheckbox">
                            <Form.Check
                                name="smallBusiness"
                                onChange={e => handleCheckbox(e, 'contact')}
                                label="Small business"
                                checked={company?.contact?.smallBusiness}
                            />
                        </Form.Group>
                    </Row>
                </Form>
            </Card>

            <Card className="mb-5">
                <Form as={Card.Body}>
                    <h2>Bank Account</h2>
                    <hr />
                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Account bank name</Form.Label>
                            <Form.Control
                                name="accountBankName"
                                onChange={e => handleChange(e, false, true)}
                                defaultValue={company?.bank?.accountBankName ?? ''}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Account iban</Form.Label>
                            <Form.Control
                                name="accountIban"
                                onChange={e => handleChange(e, false, true)}
                                defaultValue={company?.bank?.accountIban ?? ''}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} className="mb-0">
                            <Form.Label>Account swift</Form.Label>
                            <Form.Control
                                name="accountSwift"
                                onChange={e => handleChange(e, false, true)}
                                defaultValue={company?.bank?.accountSwift ?? ''}
                            />
                        </Form.Group>
                        <Form.Group as={Col} className="mb-0">
                            <Form.Label>Account payment type</Form.Label>
                            <Form.Control
                                name="accountPaymentType"
                                onChange={e => handleChange(e, false, true)}
                                defaultValue={company?.bank?.accountPaymentType ?? ''}
                            />
                        </Form.Group>
                    </Row>
                </Form>
            </Card>
            <Button onClick={editCreateCompany} size="lg" className="">
                Save
            </Button>
        </>
    );
}
